import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, getImageUrl } from '../utils/api';

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  biography: string;
  yearsOfExperience: number;
  imagePath: string | null;
  active: boolean;
  hireDate: string;
  teacherCode: number;
  whatsApp: string | null;
  facebook: string | null;
  tikTok: string | null;
  youTube: string | null;
  password: string;
}

interface Subject {
  id: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  gradeId: string;
  gradeName: string;
  teacherCount: number;
  courseCount: number;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  roleName: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
}

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  description: string;
  imagePath: string | null;
  isOpenToAll: boolean;
  subjectName: string | null;
  gradeName: string | null;
  // New: Support multiple teachers
  teacherIds?: string[];
  teachers?: Array<{
    id: string;
    name: string;
    email: string;
    isPrimary: boolean;
  }>;
}

interface CourseFilters {
  courseName: string;
  grade: string;
  active: string;
  term: string;
}

interface PaginatedResponse<T> {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  data: T[];
}

const TeacherDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'details' | 'subjects' | 'courses'>('details');
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // Subjects tab state
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectsError, setSubjectsError] = useState<string | null>(null);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [availableSubjects, setAvailableSubjects] = useState<Subject[]>([]);
  const [selectedGradeId, setSelectedGradeId] = useState<string>('');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>('');
  const [isAssigningSubject, setIsAssigningSubject] = useState(false);
  const [subjectToUnassign, setSubjectToUnassign] = useState<Subject | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // Courses tab state
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [courseFilters, setCourseFilters] = useState<CourseFilters>({
    courseName: '',
    grade: '',
    active: '',
    term: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Maximum file size (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  // Allowed file types
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    const file = e.target.files?.[0];
    
    if (!file) {
      setImageFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setImageError('Please select a valid image file (JPEG, PNG, or GIF)');
      e.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image size should be less than 5MB');
      e.target.value = '';
      return;
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setImageFile(file);

    // Cleanup preview URL when component unmounts
    return () => URL.revokeObjectURL(objectUrl);
  };

  // Fetch teacher data
  const fetchTeacher = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    
    try {
      const response = await authFetch(apiUrl(`Teacher/${id}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeacher(data.data);
        if (data.data.imagePath) {
          setPreviewUrl(getImageUrl(data.data.imagePath));
        }
      } else {
        throw new Error(data.message || 'Failed to fetch teacher details');
      }
    } catch (error) {
      console.error('Error fetching teacher:', error);
      setError('Failed to load teacher details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch teacher subjects
  const fetchTeacherSubjects = async () => {
    if (!id) return;
    
    setLoadingSubjects(true);
    setSubjectsError(null);
    
    try {
      const response = await authFetch(apiUrl(`Teacher/${id}/subjects`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubjects(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch teacher subjects');
      }
    } catch (error) {
      console.error('Error fetching teacher subjects:', error);
      setSubjectsError('Failed to load teacher subjects. Please try again later.');
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch grades for subject assignment
  const fetchGrades = async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch grades');
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setSaveMessage({
        type: 'error',
        text: 'Failed to load grades. Please try again later.'
      });
    }
  };

  // Fetch subjects for selected grade
  const fetchSubjectsForGrade = async (gradeId: string) => {
    try {
      const response = await authFetch(apiUrl(`Subject?gradeId=${gradeId}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAvailableSubjects(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subjects');
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSaveMessage({
        type: 'error',
        text: 'Failed to load subjects. Please try again later.'
      });
    }
  };

  // Handle grade selection
  const handleGradeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gradeId = e.target.value;
    setSelectedGradeId(gradeId);
    setSelectedSubjectId('');
    if (gradeId) {
      fetchSubjectsForGrade(gradeId);
    } else {
      setAvailableSubjects([]);
    }
  };

  // Handle subject unassignment
  const handleUnassignSubject = async (subject: Subject) => {
    if (!id) return;
    
    setIsUnassigning(true);
    setSaveMessage(null);
    
    try {
      const response = await authFetch(
        apiUrl(`Teacher/${id}/unassign-subject/${subject.id}`),
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إلغاء تعيين المادة بنجاح'
        });
        setSubjectToUnassign(null);
        // Refresh subjects list
        fetchTeacherSubjects();
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إلغاء تعيين المادة'
        });
      }
    } catch (error) {
      console.error('Error unassigning subject:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إلغاء تعيين المادة. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    } finally {
      setIsUnassigning(false);
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  // Handle subject assignment
  const handleAssignSubject = async () => {
    if (!id || !selectedSubjectId) return;
    
    setIsAssigningSubject(true);
    setSaveMessage(null);
    
    try {
      const response = await authFetch(
        apiUrl(`Teacher/${id}/assign-subject/${selectedSubjectId}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({
            teacherId: id,
            subjectId: selectedSubjectId
          })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم تعيين المادة للمعلم بنجاح'
        });
        setShowAddSubjectModal(false);
        fetchTeacherSubjects();
        // Reset form
        setSelectedGradeId('');
        setSelectedSubjectId('');
        setAvailableSubjects([]);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في تعيين المادة للمعلم'
        });
      }
    } catch (error) {
      console.error('Error assigning subject:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في تعيين المادة للمعلم. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    } finally {
      setIsAssigningSubject(false);
    }
  };

  // Fetch teacher courses
  const fetchTeacherCourses = async () => {
    if (!id) return;
    
    setLoadingCourses(true);
    setCoursesError(null);
    
    try {
      // Prepare URL with query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('TeacherId', id);
      queryParams.append('pageNumber', currentPage.toString());
      queryParams.append('pageSize', itemsPerPage.toString());
      
      if (courseFilters.courseName) queryParams.append('CourseName', courseFilters.courseName);
      if (courseFilters.grade) queryParams.append('Grade', courseFilters.grade);
      if (courseFilters.active) queryParams.append('Active', courseFilters.active);
      if (courseFilters.term) queryParams.append('Term', courseFilters.term);

      const response = await authFetch(apiUrl(`Course/all?${queryParams.toString()}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch courses');
      const data = await response.json();
      
      if (data.success) {
        const courseData = data.data;
        setTotalItems(courseData.totalCount);
        setTotalPages(courseData.totalPages);
        setCourses(courseData.data);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCoursesError('Failed to load courses. Please try again later.');
      setCourses([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Handle course page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Handle course search
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Reset course filters
  const handleResetFilters = () => {
    setCourseFilters({
      courseName: '',
      grade: '',
      active: '',
      term: '',
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    if (id) {
      fetchTeacher();
      if (activeTab === 'subjects') {
        fetchTeacherSubjects();
      } else if (activeTab === 'courses') {
        fetchTeacherCourses();
      }
    }
  }, [id, activeTab, currentPage, courseFilters]);

  // Fetch grades when add subject modal is opened
  useEffect(() => {
    if (showAddSubjectModal) {
      fetchGrades();
    }
  }, [showAddSubjectModal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (teacher) {
      if (name === 'yearsOfExperience' || name === 'teacherCode') {
        setTeacher({
          ...teacher,
          [name]: parseInt(value) || 0
        });
      } else if (name === 'active') {
        setTeacher({
          ...teacher,
          active: value === 'true'
        });
      } else {
        setTeacher({
          ...teacher,
          [name]: value
        });
      }
    }
  };

  const handleSaveChanges = async () => {
    if (!teacher) return;
    
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('id', teacher.id);
      formData.append('firstName', teacher.firstName);
      formData.append('lastName', teacher.lastName);
      formData.append('email', teacher.email);
      formData.append('phoneNumber', teacher.phoneNumber);
      formData.append('password', teacher.password || '');
      formData.append('biography', teacher.biography || '');
      formData.append('yearsOfExperience', teacher.yearsOfExperience?.toString() || '0');
      formData.append('teacherCode', teacher.teacherCode?.toString() || '0');
      formData.append('whatsApp', teacher.whatsApp || '');
      formData.append('facebook', teacher.facebook || '');
      formData.append('tikTok', teacher.tikTok || '');
      formData.append('youTube', teacher.youTube || '');
      formData.append('active', teacher.active.toString());
      formData.append('hireDate', teacher.hireDate || new Date().toISOString());
      
      if (imageFile) {
        const blob = new Blob([imageFile], { type: imageFile.type });
        formData.append('ImageFile', blob, imageFile.name);
      }

      const response = await authFetch(apiUrl(`Teacher/${teacher.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم تحديث بيانات المدرس بنجاح'
        });
        
        // Update local state with the latest data
        setTeacher(data.data);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في تحديث بيانات المدرس'
        });
      }
    } catch (error) {
      console.error('Error saving teacher:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في تحديث بيانات المدرس. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    } finally {
      setIsSaving(false);
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Save/Error Message Toast */}
        {saveMessage && (
          <div className={`fixed top-4 left-4 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center ${
            saveMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {saveMessage.type === 'success' ? (
              <CheckIcon className="h-5 w-5 ml-2" />
            ) : (
              <XMarkIcon className="h-5 w-5 ml-2" />
            )}
            {saveMessage.text}
          </div>
        )}

        {/* Loading States */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Errors */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">خطأ في تحميل البيانات</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!loading && !error && teacher && (
          <>
            <div className="flex items-center space-x-4 mb-6">
              <button
                onClick={() => navigate('/teachers')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-5 h-5 ml-1" />
                العودة إلى المعلمين
              </button>
              <div>
                <h1 className="text-3xl font-bold text-dark">{teacher.firstName} {teacher.lastName}</h1>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'details'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    التفاصيل
                  </button>
                  <button
                    onClick={() => setActiveTab('subjects')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'subjects'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    المواد
                  </button>
                  <button
                    onClick={() => setActiveTab('courses')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'courses'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    الدورات
                  </button>
                </nav>
              </div>

              {activeTab === 'details' && (
                <div className="p-6">
                  <form className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          الاسم الأول
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={teacher.firstName}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          الاسم الأخير
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={teacher.lastName}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          البريد الإلكتروني
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={teacher.email}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          رقم الهاتف
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={teacher.phoneNumber}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          كلمة المرور
                        </label>
                        <input
                          type="text"
                          name="password"
                          value={teacher.password || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="كلمة المرور"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          سنوات الخبرة
                        </label>
                        <input
                          type="number"
                          name="yearsOfExperience"
                          value={teacher.yearsOfExperience || 0}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          كود المعلم
                        </label>
                        <input
                          type="number"
                          name="teacherCode"
                          value={teacher.teacherCode || 0}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          واتساب
                        </label>
                        <input
                          type="text"
                          name="whatsApp"
                          value={teacher.whatsApp || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="رقم الواتساب"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          فيسبوك
                        </label>
                        <input
                          type="url"
                          name="facebook"
                          value={teacher.facebook || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="رابط الفيسبوك"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          تيك توك
                        </label>
                        <input
                          type="url"
                          name="tikTok"
                          value={teacher.tikTok || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="رابط التيك توك"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          يوتيوب
                        </label>
                        <input
                          type="url"
                          name="youTube"
                          value={teacher.youTube || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                          placeholder="رابط اليوتيوب"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          الحالة
                        </label>
                        <select
                          name="active"
                          value={teacher.active.toString()}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        >
                          <option value="true">نشط</option>
                          <option value="false">غير نشط</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        السيرة الذاتية
                      </label>
                      <textarea
                        name="biography"
                        value={teacher.biography || ''}
                        onChange={handleInputChange}
                        rows={4}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">الصورة الشخصية</label>
                      <div className="mt-1 flex flex-col items-center">
                        {!teacher.imagePath && !previewUrl ? (
                          <div className="w-full flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                              <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                                aria-hidden="true"
                              >
                                <path
                                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <div className="flex text-sm text-gray-600">
                                <label
                                  htmlFor="image-upload"
                                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                                >
                                  <span>تحميل صورة</span>
                                  <input
                                    id="image-upload"
                                    name="ImageFile"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleImageChange}
                                  />
                                </label>
                                <p className="pr-2">أو اسحب وأفلت</p>
                              </div>
                              <p className="text-xs text-gray-500">PNG, JPG, GIF حتى 5MB</p>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 w-full">
                            <div className="relative">
                              <img
                                src={previewUrl || (teacher.imagePath ? getImageUrl(teacher.imagePath) : undefined)}
                                alt={`${teacher.firstName} ${teacher.lastName}`}
                                className="w-full max-h-64 rounded-md border border-gray-200 object-contain"
                                onError={(e: any) => {
                                  const imgElement = e.target as HTMLImageElement;
                                  if (imgElement) {
                                    imgElement.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                                onClick={() => {
                                  setImageFile(null);
                                  setPreviewUrl(null);
                                  if (teacher.imagePath) {
                                    setTeacher({ ...teacher, imagePath: null });
                                  }
                                }}
                                title="حذف الصورة"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {imageError && (
                          <p className="mt-2 text-sm text-red-600">{imageError}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-start gap-4">
                      <button
                        type="button"
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {isSaving ? (
                          <>
                            <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري الحفظ...
                          </>
                        ) : (
                          <>
                            <CheckIcon className="h-4 w-4 ml-2" />
                            حفظ التغييرات
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/teachers')}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        إلغاء
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'subjects' && (
                <div className="p-3 sm:p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold">مواد المعلم</h2>
                    <button
                      onClick={() => setShowAddSubjectModal(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                               shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                               focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      إضافة مادة
                    </button>
                  </div>

                  {loadingSubjects ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : subjectsError ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <XMarkIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{subjectsError}</p>
                        </div>
                      </div>
                    </div>
                  ) : subjects.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد مواد</h3>
                      <p className="mt-1 text-sm text-gray-500">ابدأ بإضافة مادة جديدة للمعلم.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr className="bg-gray-50">
                              <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                اسم المادة
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                الوصف
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                المستوى
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                الحالة
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                الإجراءات
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {subjects.map((subject) => (
                              <tr key={subject.id} className="hover:bg-gray-50">
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{subject.name}</div>
                                </td>
                                <td className="px-4 sm:px-6 py-4">
                                  <div className="text-sm text-gray-500 max-w-xs truncate">{subject.description}</div>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{subject.gradeName}</div>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    subject.active
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {subject.active ? 'نشط' : 'غير نشط'}
                                  </span>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setSubjectToUnassign(subject);
                                    }}
                                    className="text-red-600 hover:text-red-900 flex items-center gap-1"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                    <span>إلغاء التعيين</span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Unassign Subject Confirmation Modal */}
              {subjectToUnassign && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full">
                    <h2 className="text-xl font-bold mb-4">إلغاء تعيين المادة</h2>
                    <p className="text-gray-600 mb-6">
                      هل أنت متأكد من إلغاء تعيين مادة <span className="font-bold text-gray-900">{subjectToUnassign.name}</span> من هذا المعلم؟
                    </p>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setSubjectToUnassign(null)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                        disabled={isUnassigning}
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={() => handleUnassignSubject(subjectToUnassign)}
                        disabled={isUnassigning}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {isUnassigning ? (
                          <>
                            <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري إلغاء التعيين...
                          </>
                        ) : (
                          'إلغاء التعيين'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Subject Modal */}
              {showAddSubjectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full">
                    <h2 className="text-xl font-bold mb-6">إضافة مادة جديدة</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          المستوى
                        </label>
                        <select
                          value={selectedGradeId}
                          onChange={handleGradeChange}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="">اختر المستوى</option>
                          {grades.map((grade) => (
                            <option key={grade.id} value={grade.id}>
                              {grade.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          المادة
                        </label>
                        <select
                          value={selectedSubjectId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedSubjectId(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg"
                          disabled={!selectedGradeId || availableSubjects.length === 0}
                        >
                          <option value="">اختر المادة</option>
                          {availableSubjects.map((subject) => (
                            <option key={subject.id} value={subject.id}>
                              {subject.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                      <button
                        onClick={() => {
                          setShowAddSubjectModal(false);
                          setSelectedGradeId('');
                          setSelectedSubjectId('');
                          setAvailableSubjects([]);
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleAssignSubject}
                        disabled={!selectedSubjectId || isAssigningSubject}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {isAssigningSubject ? (
                          <>
                            <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري التعيين...
                          </>
                        ) : (
                          'تعيين المادة'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'courses' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold mb-4">دورات المعلم</h2>
                    
                    {/* Course Filters */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            اسم الدورة
                          </label>
                          <input
                            type="text"
                            value={courseFilters.courseName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCourseFilters({ ...courseFilters, courseName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="البحث في اسم الدورة..."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الحالة
                          </label>
                          <select
                            value={courseFilters.active}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCourseFilters({ ...courseFilters, active: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">جميع الحالات</option>
                            <option value="true">نشط</option>
                            <option value="false">غير نشط</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الفصل الدراسي
                          </label>
                          <select
                            value={courseFilters.term}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCourseFilters({ ...courseFilters, term: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">جميع الفصول</option>
                            <option value="First">الأول</option>
                            <option value="Second">الثاني</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 mt-4">
                        <button
                          onClick={handleSearch}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          بحث
                        </button>
                        <button
                          onClick={handleResetFilters}
                          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                        >
                          إعادة تعيين
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Courses Table */}
                  {loadingCourses ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : coursesError ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <XMarkIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{coursesError}</p>
                        </div>
                      </div>
                    </div>
                  ) : courses.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد دورات</h3>
                      <p className="mt-1 text-sm text-gray-500">لم يتم العثور على أي دورات للمعلم.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-50">
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              اسم الدورة
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الوصف
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الفصل الدراسي
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              السعر
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الحالة
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              مفتوح للجميع
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المدرسون
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {courses.map((course) => {
                            const teacherCount = course.teachers?.length || course.teacherIds?.length || 0;
                            return (
                              <tr key={course.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{course.courseName}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-500 max-w-xs truncate">{course.description}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {course.term === 'First' ? 'الأول' :
                                     course.term === 'Second' ? 'الثاني' :
                                     course.term === 'Summer' ? 'الصيفي' : course.term}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{course.price} </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    course.active
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {course.active ? 'نشط' : 'غير نشط'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    course.isOpenToAll
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {course.isOpenToAll ? 'نعم' : 'لا'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-600">
                                    {course.teachers && course.teachers.length > 0 ? (
                                      <div className="flex flex-col gap-1">
                                        {course.teachers.map((teacher) => (
                                          <div key={teacher.id} className="flex items-center gap-1">
                                            <span>{teacher.name}</span>
                                            {teacher.isPrimary && (
                                              <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">Primary</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : teacherCount > 0 ? (
                                      <span>{teacherCount} مدرس</span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {!loadingCourses && courses.length > 0 && totalPages > 1 && (
                    <div className="mt-6 flex justify-center">
                      <nav className="flex items-center space-x-reverse space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-1 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          السابق
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          التالي
                        </button>
                      </nav>
                    </div>
                  )}

                  {/* Results Summary */}
                  {!loadingCourses && courses.length > 0 && (
                    <div className="mt-4 text-sm text-gray-500 text-center">
                      عرض {courses.length} من {totalItems} دورة
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeacherDetails;
